
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { Factory, ArrowLeft, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const IndustrialAutomationPage = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="mb-6">
              <Link to="/services" className="text-muted-foreground hover:text-primary flex items-center transition-colors">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to all services
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-12 mb-16">
              <div className="md:col-span-2">
                <div className="animate-fade-in">
                  <div className="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400 p-3 rounded-xl inline-block mb-4">
                    <Factory className="h-10 w-10" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                    Intelligent Industrial <span className="text-primary">Automation</span>
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Smart manufacturing and production systems powered by AI and IoT technologies.
                  </p>
                </div>

                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p>
                    Industrial automation is transforming manufacturing and production processes, making them more efficient, reliable, and intelligent. Our industrial automation solutions combine AI, IoT, and robotics to create smart factories and production systems.
                  </p>
                  
                  <h2>Our Approach</h2>
                  <p>
                    We develop custom industrial automation solutions that integrate with your existing systems and processes. Our approach focuses on creating intelligent, connected systems that enhance efficiency, quality, and safety while reducing costs and downtime.
                  </p>
                  
                  <h2>Key Benefits</h2>
                  <ul>
                    <li>Increase production efficiency and throughput</li>
                    <li>Improve product quality and consistency</li>
                    <li>Reduce waste and resource consumption</li>
                    <li>Minimize downtime through predictive maintenance</li>
                    <li>Enhance worker safety and productivity</li>
                    <li>Gain real-time visibility and control over production processes</li>
                  </ul>
                </div>
              </div>

              <div className="md:col-span-1">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-subtle border border-gray-100 dark:border-gray-700 sticky top-32">
                  <h3 className="text-xl font-semibold mb-4">Our Industrial Automation Services</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Smart Manufacturing</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Process Optimization</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Predictive Maintenance</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Quality Control Systems</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Digital Twins</span>
                    </li>
                    <li className="flex items-start">
                      <ChevronRight className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                      <span>Supply Chain Automation</span>
                    </li>
                  </ul>

                  <div className="mt-8">
                    <Link to="/contact">
                      <Button className="w-full">
                        Contact Us
                        <ArrowUpRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-amber-50 dark:from-red-950/30 dark:to-amber-950/30 p-8 rounded-2xl mb-16">
              <h2 className="text-2xl font-semibold mb-4">Ready to transform your industrial processes?</h2>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our industrial automation solutions can help you improve efficiency, quality, and profitability.
              </p>
              <Link to="/contact">
                <Button>
                  Get Started
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default IndustrialAutomationPage;
